import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Check, Sparkles, Loader2, Clock } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";

const FEATURES = {
  starter: [
    { en: "1 vacancy activation for 30 days", fr: "1 activation de poste pour 30 jours" },
    { en: "Subscription valid 12 months", fr: "Abonnement valable 12 mois" },
    { en: "AI candidate matching", fr: "Matching candidats par IA" },
    { en: "Basic application management", fr: "Gestion de base des candidatures" },
  ],
  growth: [
    { en: "3 vacancy activations, 30 days each", fr: "3 activations de postes, 30 jours chacune" },
    { en: "Subscription valid 12 months", fr: "Abonnement valable 12 mois" },
    { en: "AI candidate matching & insights", fr: "Matching IA & insights candidats" },
    { en: "Employer career page", fr: "Page carrière employeur" },
    { en: "Priority in candidate suggestions", fr: "Priorité dans les suggestions" },
  ],
  priority: [
    { en: "5 vacancy activations, 30 days each", fr: "5 activations de postes, 30 jours chacune" },
    { en: "Subscription valid 12 months", fr: "Abonnement valable 12 mois" },
    { en: "AI matching + qualification scoring", fr: "Matching IA + scoring de qualification" },
    { en: "Advanced analytics & reports", fr: "Analytique & rapports avancés" },
    { en: "Sponsored placement (1 slot / month)", fr: "Placement sponsorisé (1 slot / mois)" },
  ],
  talent: [
    { en: "10 vacancy activations, 30 days each", fr: "10 activations de postes, 30 jours chacune" },
    { en: "Subscription valid 12 months", fr: "Abonnement valable 12 mois" },
    { en: "Full talent pipeline (CV database access)", fr: "Pipeline talents complet (accès CVthèque)" },
    { en: "Dedicated account manager", fr: "Chargé de compte dédié" },
    { en: "Multi-branch career pages", fr: "Pages carrière multi-sites" },
  ],
};

export default function EmployerPlansPage() {
  const { lang } = useI18n();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    if (!token) { navigate("/register?role=employer"); return; }
    if (user && user.role !== "employer") { navigate("/"); return; }
    axios.get(`${API}/plans`).then(r => setPlans(r.data.plans || [])).catch(() => {});
  }, [token, user, navigate]);

  const request = async (slug) => {
    setSubmitting(slug);
    try {
      await axios.post(`${API}/employer/request-quote`, { plan_slug: slug }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "fr" ? "Devis demandé !" : "Quote requested!");
      navigate("/pending-approval");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSubmitting(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 md:py-16" data-testid="employer-plans-page">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#1A8FE3]/8 border border-[#1A8FE3]/20 px-3 py-1 text-xs text-[#1A8FE3] font-medium mb-4">
          <Clock className="h-3 w-3" />
          {lang === "fr" ? "Étape 2 sur 3 — Choisissez votre plan" : "Step 2 of 3 — Pick your plan"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">
          {lang === "fr" ? "Choisissez le plan qui vous correspond" : "Choose the plan that fits you"}
        </h1>
        <p className="mt-3 text-slate-600">
          {lang === "fr"
            ? "Chaque crédit active une offre pendant 30 jours. Abonnement valable 12 mois."
            : "Each credit activates a vacancy for 30 days. Subscription valid for 12 months."}
        </p>
      </div>

      {/* Mobile: stacked cards | Desktop: side-by-side grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((p) => {
          const feats = FEATURES[p.slug] || [];
          const isRecommended = p.slug === "growth";
          return (
            <Card
              key={p.slug}
              data-testid={`plan-card-${p.slug}`}
              className={`relative p-6 flex flex-col ${isRecommended ? "border-[#1A8FE3] border-2 shadow-lg" : "border-slate-200"}`}
            >
              {isRecommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1A8FE3] text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> {lang === "fr" ? "Recommandé" : "Recommended"}
                </div>
              )}
              <div className="text-center pb-4 border-b border-slate-100">
                <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                <div className="mt-3">
                  <span className="text-4xl font-bold text-[#1A8FE3]" data-testid={`plan-credits-${p.slug}`}>{p.credits}</span>
                  <span className="text-slate-500 ml-1">{lang === "fr" ? "crédit(s) job" : "job credit(s)"}</span>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {feats.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-700">
                    <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{lang === "fr" ? f.fr : f.en}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => request(p.slug)}
                disabled={submitting !== null}
                data-testid={`plan-request-${p.slug}`}
                className={`mt-6 w-full rounded-full h-11 ${isRecommended ? "bg-[#1A8FE3] hover:bg-[#157AC0] text-white" : "bg-white border-2 border-slate-200 text-slate-900 hover:border-[#1A8FE3] hover:text-[#1A8FE3]"}`}
              >
                {submitting === p.slug ? <Loader2 className="h-4 w-4 animate-spin" /> : (lang === "fr" ? "Demander un devis" : "Request a Quote")}
              </Button>
            </Card>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-500 mt-8">
        {lang === "fr"
          ? "Après votre demande, notre équipe examinera votre compte et activera votre plan sous 24h ouvrées."
          : "After you request, our team reviews your account and activates your plan within 24 business hours."}
      </p>
    </div>
  );
}
